import { pluralizeWithCount } from "@common/helpers/string";
import type { WorkSkipReason } from "../editing/workItemEditing";

export type WorkBulkAction = "update" | "cancel";

const PAST_TENSE: Record<WorkBulkAction, string> = {
    update: "Updated",
    cancel: "Canceled",
};

const SKIP_REASON_COPY: Record<WorkSkipReason, string> = {
    doneOrCanceled: "done or canceled",
    noOpenTasks: "with no open tasks",
};

const SKIP_REASONS: readonly WorkSkipReason[] = [
    "doneOrCanceled",
    "noOpenTasks",
];

/**
 * The one toast for a bulk edit or Cancel: `Updated 2 work items, 1 failed`,
 * `Failed to update work item`, plus how many items it skipped and why, as
 * in `skipped 1 done or canceled and 2 with no open tasks`.
 */
export const summarizeWorkBulkResult = ({
    action,
    succeededCount,
    failedCount,
    skippedCounts,
}: {
    action: WorkBulkAction;
    succeededCount: number;
    failedCount: number;
    skippedCounts: Record<WorkSkipReason, number>;
}) => {
    const parts: string[] = [];
    if (succeededCount > 0) {
        parts.push(
            `${PAST_TENSE[action]} ${pluralizeWithCount("work item", succeededCount)}`,
        );
        if (failedCount > 0) {
            parts.push(`${failedCount} failed`);
        }
    } else if (failedCount > 0) {
        parts.push(
            failedCount === 1
                ? `Failed to ${action} work item`
                : `Failed to ${action} ${pluralizeWithCount("work item", failedCount)}`,
        );
    }
    const skips = SKIP_REASONS.filter(
        (reason) => skippedCounts[reason] > 0,
    ).map((reason) => `${skippedCounts[reason]} ${SKIP_REASON_COPY[reason]}`);
    if (skips.length > 0) {
        parts.push(
            `${parts.length > 0 ? "skipped" : "Skipped"} ${skips.join(" and ")}`,
        );
    }
    return { message: parts.join(", "), isError: failedCount > 0 };
};
